import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowUpRight,
    Star,
    Briefcase,
    BookOpen,
    Shield,
    Quote,
    Skull,
    RefreshCw,
    Compass,
} from "lucide-react";
import SEO, { organizationJsonLd, websiteJsonLd } from "@/components/common/SEO";
import Stamp from "@/components/common/Stamp";
import { api } from "@/lib/api";
import {
    SITE_META,
    PRODUCTS,
    PLANES,
    EQUIPO,
    KPIS,
} from "@/data/staticContent";

export default function AutodestruccionSA() {
    const [excusa, setExcusa] = useState("Pulsa el botón para ver tu excusa del día.");
    const [posts, setPosts] = useState([]);
    const [antipoesiaPreview, setAntipoesiaPreview] = useState([]);
    const [glosarioPreview, setGlosarioPreview] = useState([]);

    useEffect(() => {
        api.get("/blog?limit=4")
            .then(({ data }) => setPosts(data))
            .catch(() => {});
        api.get("/antipoesia?limit=2")
            .then(({ data }) => setAntipoesiaPreview(data))
            .catch(() => {});
        api.get("/glosario?limit=6")
            .then(({ data }) => setGlosarioPreview(data))
            .catch(() => {});
    }, []);

    async function fetchExcusa() {
        try {
            const { data } = await api.get("/excusa");
            setExcusa(data.excusa);
        } catch {
            setExcusa("Hasta el sistema falló. Otra excusa para no responder.");
        }
    }

    return (
        <>
            <SEO
                title="Autodestrucción S.A. — La empresa del vacío"
                description="Capítulo 3 del libro 'El Principio del Mapa Inverso'. Soluciones llave en mano para vacíos profundos. 20 años garantizando resultados destructivos."
                path="/autodestruccion-sa"
                keywords="adicción, recuperación, antipoesía, nitanadicto, Chile, cicatrices, sobriedad, autoengaño, recaída"
                jsonLd={[organizationJsonLd, websiteJsonLd]}
            />

            {/* ============ HERO ============ */}
            <section
                className="relative overflow-hidden grain"
                data-testid="hero-section"
            >
                {/* Breadcrumb desde Mapa Inverso */}
                <div className="border-b border-[var(--line)] bg-[#0a0a0a]">
                    <div className="container-x py-3 flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em]">
                        <Link to="/" className="text-[var(--ink-muted)] hover:text-[var(--warning)]" data-testid="breadcrumb-home">
                            ← El Mapa Inverso
                        </Link>
                        <span className="text-[var(--ink-dim)]">·</span>
                        <span className="text-[var(--warning)]">Capítulo 3 · Autodestrucción S.A.</span>
                        <span className="dotted-div flex-1 min-w-[40px]" />
                        <Link to="/libro" className="text-[var(--ink-muted)] hover:text-[var(--danger)]" data-testid="breadcrumb-libro">
                            Descargar el libro completo →
                        </Link>
                    </div>
                </div>

                {/* Hazard top */}
                <div className="hazard-stripes h-2 w-full" />

                <div className="container-x py-16 md:py-24 lg:py-32 relative z-10">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        {/* Left */}
                        <div className="lg:col-span-8">
                            <div className="flex items-center gap-3 mb-6 flex-wrap">
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)]">
                                    EXP. AD-{new Date().getFullYear()}/01
                                </span>
                                <span className="dotted-div flex-1 min-w-[60px]" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)]">
                                    Confidencial · Terminal
                                </span>
                            </div>

                            <h1
                                className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[4.5rem] xl:text-[5.5rem] 2xl:text-[6.5rem] leading-[0.85] tracking-tight mb-6"
                                data-testid="hero-headline"
                            >
                                <span className="block text-[var(--paper-2)]">
                                    Autodestrucción
                                </span>
                                <span className="block">
                                    <span className="text-[var(--danger)]">
                                        S
                                    </span>
                                    <span className="text-[var(--paper-2)]">
                                        .
                                    </span>
                                    <span className="text-[var(--danger)]">
                                        A
                                    </span>
                                    <span className="text-[var(--paper-2)]">
                                        .
                                    </span>
                                    <span className="font-hand text-[var(--warning)] text-3xl md:text-5xl lg:text-4xl xl:text-5xl ml-4 -rotate-6 inline-block align-middle">
                                        en quiebra interna
                                    </span>
                                </span>
                            </h1>

                            <p className="font-mono text-base md:text-lg text-[var(--paper)] max-w-xl mb-4 leading-relaxed">
                                <strong className="text-[var(--paper-2)]">
                                    Soluciones llave en mano para vacíos
                                    profundos.
                                </strong>{" "}
                                20 años garantizando resultados destructivos.
                            </p>
                            <p className="font-mono text-sm text-[var(--ink-muted)] max-w-lg mb-10">
                                Bienvenido a la empresa que nunca quisiste
                                contratar, pero probablemente ya lo hiciste.
                                Aquí las cicatrices pidieron hablar y les dimos
                                tribuna.
                            </p>

                            <div className="flex flex-wrap gap-4">
                                <Link
                                    to="/productos"
                                    className="btn-brutal"
                                    data-testid="hero-cta-productos"
                                >
                                    <Briefcase className="w-4 h-4" />
                                    Ver el catálogo
                                </Link>
                                <Link
                                    to="/antipoesia"
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="hero-cta-antipoesia"
                                >
                                    <BookOpen className="w-4 h-4" />
                                    Antipoesía
                                </Link>
                                <Link
                                    to="/glosario"
                                    className="btn-brutal btn-brutal-danger"
                                    data-testid="hero-cta-glosario"
                                >
                                    Glosario del Adicto
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>

                        {/* Right — Avatar with stamps */}
                        <div className="lg:col-span-4 relative">
                            <div className="relative aspect-square max-w-md mx-auto">
                                <div className="absolute inset-0 bg-[var(--danger)] translate-x-4 translate-y-4" />
                                <img
                                    src={SITE_META.avatarUrl}
                                    alt="Retrato pop-art de @nitanadicto, fundador de Autodestrucción S.A."
                                    className="relative w-full h-full object-cover border-2 border-[var(--paper-2)]"
                                    data-testid="hero-avatar"
                                />
                                <div className="absolute -top-4 -right-4 z-10">
                                    <Stamp size="lg" rotation={12} color="danger">
                                        Caso 001
                                    </Stamp>
                                </div>
                                <div className="absolute -bottom-3 -left-3 z-10">
                                    <Stamp size="md" rotation={-8} color="warning">
                                        Cliente frecuente
                                    </Stamp>
                                </div>
                                {/* Annotations */}
                                <span className="absolute top-1/2 -right-12 hidden md:block font-hand text-[var(--warning)] text-2xl -rotate-12">
                                    ← evidencia
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* KPI Marquee bottom */}
                <div className="border-y-2 border-[var(--paper-2)] bg-[#0a0a0a] py-5 mt-10">
                    <div className="marquee">
                        <div className="marquee-track font-mono text-[11px] md:text-sm uppercase tracking-[0.25em] text-[var(--paper-2)]">
                            {[...Array(4)].flatMap((_, i) =>
                                KPIS.map((k, j) => (
                                    <span
                                        key={`${i}-${j}`}
                                        className="inline-flex items-center gap-3"
                                    >
                                        <span className="text-[var(--danger)] font-display text-2xl">
                                            {k.number}
                                        </span>
                                        <span>{k.label}</span>
                                        <span className="text-[var(--ink-dim)]">
                                            ✕
                                        </span>
                                    </span>
                                )),
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ PRODUCTS ============ */}
            <section
                id="productos"
                className="py-20 md:py-28 relative grain"
                data-testid="products-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 mb-16">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4">
                                Catálogo · Sección 01
                            </p>
                            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                                Nuestros{" "}
                                <span className="text-[var(--danger)]">
                                    productos
                                </span>
                                <span className="font-hand text-[var(--warning)] text-3xl -rotate-3 inline-block ml-2">
                                    ¿clientes?
                                </span>
                            </h2>
                        </div>
                        <div className="lg:col-span-5 flex items-end">
                            <p className="font-mono text-sm text-[var(--ink-muted)] leading-relaxed">
                                Lo que vendemos no es una sustancia: es una
                                vida vacía con servicio técnico permanente.
                                Entrega 24/7. Sin devoluciones. Sin soporte
                                emocional.
                            </p>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {PRODUCTS.map((p, i) => (
                            <article
                                key={p.slug}
                                className="dossier group hover:border-[var(--danger)] transition-colors"
                                data-testid={`product-card-${p.slug}`}
                            >
                                <div className="dossier-header">
                                    <span>{p.codeName}</span>
                                    <AlertTriangle className="w-3.5 h-3.5 text-[var(--danger)]" />
                                </div>
                                <div className="aspect-[4/5] overflow-hidden bg-black relative">
                                    <img
                                        src={p.image}
                                        alt={`Representación conceptual de ${p.name}, ${p.role}`}
                                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                        loading="lazy"
                                    />
                                    <div className="absolute top-3 right-3">
                                        <Stamp
                                            size="sm"
                                            rotation={-8 + i * 4}
                                        >
                                            {p.warning.split(":")[1]}
                                        </Stamp>
                                    </div>
                                </div>
                                <div className="p-5">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-dim)] mb-1">
                                        {p.role}
                                    </p>
                                    <h3 className="font-display text-2xl mb-2 text-[var(--paper-2)]">
                                        {p.name}
                                    </h3>
                                    <p className="font-mono text-xs text-[var(--paper)] mb-3 leading-relaxed">
                                        {p.tagline}
                                    </p>
                                    <p className="font-mono text-[11px] text-[var(--ink-muted)] leading-relaxed">
                                        {p.description}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>

                    <div className="mt-12 text-center">
                        <Link
                            to="/productos"
                            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[var(--paper-2)] hover:text-[var(--danger)] border-b border-[var(--paper-2)] hover:border-[var(--danger)] pb-1"
                            data-testid="products-see-all"
                        >
                            Ver catálogo completo <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============ MAPA INVERSO CTA ============ */}
            <section
                className="py-20 md:py-28 relative border-y-2 border-[var(--warning)] bg-[#0a0a0a] overflow-hidden"
                data-testid="mapa-inverso-cta-section"
            >
                <div className="hazard-stripes-thin h-2 absolute top-0 inset-x-0" />
                <div className="container-x relative z-10">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4" style={{ color: "var(--warning)" }}>
                                <span style={{ background: "var(--warning)" }} className="inline-block w-7 h-px" />
                                Nuevo · expediente interactivo · 03
                            </p>
                            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl mb-4 leading-[0.9]">
                                <span className="text-[var(--paper-2)]">Dibuja tu</span>{" "}
                                <span className="text-[var(--warning)]">Mapa Inverso</span>
                            </h2>
                            <p className="font-mono text-base text-[var(--paper)] leading-relaxed mb-3 max-w-xl">
                                7 pasos. Una ruta antigua que ya conoces y otra
                                contraria que vas a construir. Al final, un PDF
                                tipo expediente para guardar o compartir con tu
                                persona segura.
                            </p>
                            <p className="font-hand text-2xl text-[var(--warning)] -rotate-1 inline-block mb-8">
                                No es terapia. Es un espejo con plan de salida.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link
                                    to="/mapa-inverso"
                                    className="btn-brutal btn-brutal-danger"
                                    data-testid="home-mapa-inverso-cta"
                                >
                                    <Compass className="w-4 h-4" />
                                    Empezar mi mapa
                                </Link>
                                <Link
                                    to="/salida"
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="home-mapa-inverso-salida"
                                >
                                    <Shield className="w-4 h-4" />
                                    Necesito ayuda ahora
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="border-2 border-[var(--danger)] p-5 bg-[#111]">
                                    <Skull className="w-6 h-6 text-[var(--danger)] mb-2" />
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-1">
                                        Mapa antiguo
                                    </p>
                                    <p className="font-display text-xl text-[var(--paper-2)]">
                                        Al Infierno
                                    </p>
                                    <p className="font-mono text-[11px] text-[var(--paper)] mt-2 leading-relaxed">
                                        Gatillos, mentiras, conductas, consecuencias.
                                    </p>
                                </div>
                                <div className="border-2 border-[var(--warning)] p-5 bg-[#111]">
                                    <Compass className="w-6 h-6 text-[var(--warning)] mb-2" />
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-1">
                                        Mapa nuevo
                                    </p>
                                    <p className="font-display text-xl text-[var(--paper-2)]">
                                        De Salida
                                    </p>
                                    <p className="font-mono text-[11px] text-[var(--paper)] mt-2 leading-relaxed">
                                        Acción contraria, límite, ancla, propósito.
                                    </p>
                                </div>
                                <div className="col-span-2 border border-dashed border-[var(--ink-dim)] p-4 bg-[#0d0d0d]">
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)]">
                                            Avance estimado
                                        </span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)]">
                                            10–15 min
                                        </span>
                                    </div>
                                    <div className="h-1.5 w-full bg-[var(--line)] relative">
                                        <div className="absolute inset-y-0 left-0 w-[68%] bg-[var(--danger)]" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="hazard-stripes-thin h-2 absolute bottom-0 inset-x-0" />
            </section>

            {/* ============ PLANS ============ */}
            <section
                className="py-20 md:py-28 bg-[#0d0d0d] border-y-2 border-[var(--paper-2)] relative"
                data-testid="plans-section"
            >
                <div className="container-x">
                    <div className="text-center mb-16">
                        <p className="eyebrow justify-center mb-4">
                            Pricing · Sección 02
                        </p>
                        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl mb-4">
                            Elige tu{" "}
                            <span className="crossed">plan ideal</span>{" "}
                            <span className="text-[var(--danger)]">camino</span>
                        </h2>
                        <p className="font-mono text-sm text-[var(--ink-muted)] max-w-xl mx-auto">
                            Tres niveles de servicio. Todos llevan al mismo
                            destino, solo cambia la velocidad.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {PLANES.map((plan, i) => (
                            <article
                                key={plan.slug}
                                className={`relative bg-[#1a1a1a] border ${plan.featured ? "border-[var(--danger)]" : "border-[var(--line)]"} p-8 transition-transform hover:-translate-y-1`}
                                data-testid={`plan-card-${plan.slug}`}
                            >
                                {plan.featured && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 hazard-stripes-thin px-3 py-1">
                                        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black">
                                            Más recomendado
                                        </span>
                                    </div>
                                )}
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-2">
                                    {plan.code}
                                </p>
                                <h3 className="font-display text-4xl md:text-5xl mb-3 text-[var(--paper-2)]">
                                    {plan.name}
                                </h3>
                                <p className="font-mono text-xs text-[var(--ink-muted)] mb-6 min-h-[2.5em]">
                                    {plan.tagline}
                                </p>
                                <div className="mb-6 pb-6 border-b border-dashed border-[var(--ink-dim)]">
                                    <p className="font-display text-2xl text-[var(--paper-2)]">
                                        {plan.priceLabel}
                                    </p>
                                    <p className="font-hand text-[var(--danger)] text-xl -rotate-1 mt-1">
                                        {plan.priceSub}
                                    </p>
                                </div>
                                <ul className="space-y-2 mb-8">
                                    {plan.bullets.map((b, k) => (
                                        <li
                                            key={k}
                                            className="font-mono text-xs text-[var(--paper)] flex gap-2"
                                        >
                                            <span className="text-[var(--danger)] flex-shrink-0">
                                                ✕
                                            </span>
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={plan.ctaPath}
                                    className={`block text-center font-mono text-xs uppercase tracking-[0.25em] py-3 border-2 transition-colors ${plan.featured ? "border-[var(--danger)] bg-[var(--danger)] text-[var(--paper-2)] hover:bg-transparent hover:text-[var(--danger)]" : "border-[var(--paper-2)] text-[var(--paper-2)] hover:bg-[var(--paper-2)] hover:text-black"}`}
                                    data-testid={`plan-cta-${plan.slug}`}
                                >
                                    {plan.cta}
                                </Link>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ TESTIMONIAL ============ */}
            <section
                className="py-20 md:py-28 relative grain"
                data-testid="testimonial-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                        <div className="lg:col-span-5">
                            <p className="eyebrow mb-4">Reseña verificada · 04</p>
                            <div className="flex gap-1 mb-6">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                        key={s}
                                        className="w-6 h-6 fill-[var(--warning)] text-[var(--warning)]"
                                    />
                                ))}
                            </div>
                            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] mb-4">
                                Cliente frecuente:{" "}
                                <span className="text-[var(--danger)]">
                                    @NitanAdicto
                                </span>
                            </h2>
                            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink-muted)] mb-6">
                                Plan Sin Retorno · Suscripción activa por más
                                de 10 años
                            </p>
                            <Link
                                to="/nelson-gonzalez-rodriguez"
                                className="btn-brutal btn-brutal-danger"
                                data-testid="testimonial-cta"
                            >
                                Leer experiencia completa
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <blockquote className="lg:col-span-7 relative">
                            <Quote className="absolute -top-4 -left-4 w-16 h-16 text-[var(--danger)] opacity-30" />
                            <p className="font-display italic text-2xl md:text-3xl leading-[1.3] text-[var(--paper-2)] mb-6">
                                "Tomé el <strong className="text-[var(--danger)] not-italic">Plan Sin Retorno</strong> hace más de una década. El servicio fue impecable: entrega inmediata, sin devoluciones y sin horario de atención."
                            </p>
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed mb-4">
                                En pocos meses perdí mi dignidad, mi salud,
                                varios años de vida útil y la confianza de mis
                                hijos. El soporte emocional fue pésimo, pero el
                                seguimiento obsesivo fue de lujo: me
                                acompañaron al baño, a las recaídas y a cada
                                promesa rota.
                            </p>
                            <p className="font-hand text-[var(--warning)] text-2xl -rotate-2">
                                — Ideal para quienes buscan llenar el vacío con
                                lo que más lo agranda.
                            </p>
                        </blockquote>
                    </div>
                </div>
            </section>

            {/* ============ TEAM ============ */}
            <section
                className="py-20 md:py-28 bg-[#0d0d0d] relative"
                data-testid="team-section"
            >
                <div className="container-x">
                    <div className="text-center mb-16">
                        <p className="eyebrow justify-center mb-4">
                            Organigrama · Sección 05
                        </p>
                        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl">
                            Nuestro <span className="text-[var(--danger)]">equipo</span>{" "}
                            ejecutivo
                        </h2>
                        <p className="font-mono text-sm text-[var(--ink-muted)] max-w-xl mx-auto mt-4">
                            Los seis departamentos que nunca me fallaron en 20
                            años. Todos somos yo. Tristemente.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {EQUIPO.map((m, i) => (
                            <article
                                key={m.name}
                                className="border border-[var(--line)] bg-[#1a1a1a] p-6 hover:border-[var(--danger)] transition-colors group"
                                data-testid={`team-card-${i}`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <Skull className="w-8 h-8 text-[var(--danger)]" />
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-dim)] text-right">
                                        ID-{String(i + 1).padStart(3, "0")}
                                    </span>
                                </div>
                                <h3 className="font-display text-2xl mb-1 text-[var(--paper-2)]">
                                    {m.name}
                                </h3>
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--warning)] mb-3">
                                    {m.person}
                                </p>
                                <p className="font-mono text-xs text-[var(--ink-muted)] leading-relaxed mb-4">
                                    {m.bio}
                                </p>
                                <div className="border-t border-dashed border-[var(--ink-dim)] pt-3 flex justify-between items-baseline">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)]">
                                        KPI
                                    </span>
                                    <span className="font-display text-lg text-[var(--danger)]">
                                        {m.kpi}
                                    </span>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ GLOSARIO PREVIEW ============ */}
            <section
                className="py-20 md:py-28 grain relative"
                data-testid="glosario-preview-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4">
                                Diccionario interno · 06
                            </p>
                            <h2 className="font-display text-5xl md:text-6xl mb-4">
                                Glosario del{" "}
                                <span className="text-[var(--danger)]">
                                    Adicto
                                </span>
                            </h2>
                            <p className="font-mono text-sm text-[var(--paper)] max-w-xl">
                                Definiciones que la RAE nunca se atrevió a
                                escribir.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex items-end justify-end">
                            <Link
                                to="/glosario"
                                className="btn-brutal"
                                data-testid="glosario-preview-cta"
                            >
                                Entrar al glosario
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
                        {glosarioPreview.map((g, i) => (
                            <div
                                key={g.term}
                                className="bg-[#0d0d0d] p-6 hover:bg-[#1a1a1a] transition-colors"
                                data-testid={`glosario-preview-${i}`}
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] mb-1">
                                    {String(i + 1).padStart(2, "0")} ·
                                    sustantivo
                                </p>
                                <h3 className="font-display text-2xl mb-2 text-[var(--warning)]">
                                    {g.term}
                                </h3>
                                <p className="font-mono text-xs text-[var(--paper)] leading-relaxed">
                                    {g.definition}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ ANTIPOESIA PREVIEW ============ */}
            <section
                className="py-20 md:py-28 bg-[#0d0d0d] relative"
                data-testid="antipoesia-preview-section"
            >
                <div className="container-x">
                    <div className="text-center mb-16">
                        <p className="eyebrow justify-center mb-4">
                            Versos sin máscara · 07
                        </p>
                        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl mb-4">
                            Antipoesía{" "}
                            <span className="text-[var(--danger)]">
                                Terapéutica
                            </span>
                        </h2>
                        <p className="font-mono text-sm text-[var(--ink-muted)] max-w-xl mx-auto">
                            Dicen que la poesía embellece la vida. La antipoesía
                            la desnuda.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {antipoesiaPreview.map((p, i) => (
                            <article
                                key={p.title}
                                className="relative pl-6 border-l-2 border-[var(--danger)] flex flex-col"
                                data-testid={`antipoesia-preview-${i}`}
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-5">
                                    Fragmento {String(i + 1).padStart(2, "0")}
                                </p>
                                <pre className="font-display italic text-lg text-[var(--paper)] whitespace-pre-wrap leading-relaxed flex-1">
                                    {p.text}
                                </pre>
                                <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--warning)] mt-5 pt-4 border-t border-dashed border-[var(--ink-dim)]">
                                    — {p.title} —
                                </h3>
                            </article>
                        ))}
                    </div>
                    <div className="text-center mt-12">
                        <Link
                            to="/antipoesia"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="antipoesia-preview-cta"
                        >
                            Leer antipoesías completas
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============ EXCUSA DEL DIA ============ */}
            <section
                className="py-16 md:py-20 grain relative"
                data-testid="excusa-section"
            >
                <div className="container-x">
                    <div className="border-2 border-dashed border-[var(--warning)] p-8 md:p-12 max-w-3xl mx-auto text-center bg-[#0a0a0a]">
                        <p className="eyebrow justify-center mb-4" style={{ color: "var(--warning)" }}>
                            <span style={{ background: "var(--warning)" }} className="inline-block w-7 h-px"></span>
                            Generador automático · 08
                        </p>
                        <h2 className="font-display text-4xl md:text-5xl mb-2">
                            Tu <span className="text-[var(--warning)]">excusa</span>{" "}
                            del día
                        </h2>
                        <p className="font-hand text-xl text-[var(--warning)] -rotate-2 inline-block mb-8">
                            Porque siempre tienes una, funcional.
                        </p>
                        <div className="min-h-[100px] flex items-center justify-center mb-6">
                            <p
                                className="font-display italic text-2xl md:text-3xl text-[var(--paper-2)] leading-tight"
                                data-testid="excusa-text"
                            >
                                "{excusa}"
                            </p>
                        </div>
                        <button
                            onClick={fetchExcusa}
                            className="btn-brutal"
                            data-testid="excusa-button"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Dame mi excusa
                        </button>
                    </div>
                </div>
            </section>

            {/* ============ BLOG PREVIEW ============ */}
            <section
                className="py-20 md:py-28 bg-[#0d0d0d] relative"
                data-testid="blog-preview-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4">
                                Archivo público · 09
                            </p>
                            <h2 className="font-display text-5xl md:text-6xl mb-4">
                                Archivo de{" "}
                                <span className="text-[var(--danger)]">
                                    Cicatrices
                                </span>
                            </h2>
                            <p className="font-mono text-sm text-[var(--paper)] max-w-xl">
                                Entradas, glosario, currículum del autoengaño,
                                mensajes iniciáticos y textos que convierten la
                                vergüenza en espejo.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex items-end justify-end">
                            <Link
                                to="/blog"
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="blog-preview-cta"
                            >
                                Ver todas las entradas
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)]">
                        {posts.slice(0, 4).map((post, i) => (
                            <Link
                                key={post.slug}
                                to={`/blog/${post.slug}`}
                                className="bg-[#111] p-6 hover:bg-[#1a1a1a] transition-colors group block"
                                data-testid={`blog-preview-${i}`}
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-3">
                                    {post.category}
                                </p>
                                <h3 className="font-display text-xl mb-3 text-[var(--paper-2)] group-hover:text-[var(--danger)] transition-colors leading-tight">
                                    {post.title}
                                </h3>
                                <p className="font-mono text-xs text-[var(--ink-muted)] leading-relaxed line-clamp-3">
                                    {post.excerpt}
                                </p>
                                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)]">
                                    Leer entrada →
                                </p>
                            </Link>
                        ))}
                        {posts.length === 0 && (
                            <p className="col-span-4 bg-[#111] p-8 font-mono text-sm text-[var(--ink-muted)] text-center">
                                Archivo cargando…
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {/* ============ SALIDA DE EMERGENCIA ============ */}
            <section
                className="py-20 md:py-28 relative"
                data-testid="salida-preview-section"
            >
                <div className="container-x">
                    <div className="hazard-stripes h-2 w-full mb-0" />
                    <div className="border-x-2 border-b-2 border-black bg-[var(--warning)] p-10 md:p-16 text-center text-black relative">
                        <Shield className="w-12 h-12 mx-auto mb-4" />
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] mb-4">
                            Salida de emergencia · Sección 10
                        </p>
                        <h2 className="font-display text-4xl md:text-6xl mb-6 leading-[0.95]">
                            ¿Te reflejó demasiado?
                        </h2>
                        <p className="font-mono text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                            Si esto dejó de parecer chiste, quizás ya no es
                            contenido: es una señal. No tienes que tocar otro
                            fondo para pedir ayuda.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/salida"
                                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-black text-[var(--warning)] px-6 py-3 hover:bg-[#1a1a1a]"
                                data-testid="salida-preview-cta-1"
                            >
                                Pedir orientación
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <a
                                href={SITE_META.socials.sinAdicciones}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] border-2 border-black px-6 py-3 hover:bg-black hover:text-[var(--warning)]"
                                data-testid="salida-preview-cta-2"
                            >
                                Ir a SinAdicciones.org
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                    <div className="hazard-stripes h-2 w-full" />
                </div>
            </section>

            {/* ============ SOBRE ============ */}
            <section
                className="py-20 md:py-28 grain relative bg-[#0a0a0a]"
                data-testid="sobre-preview-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-5 relative">
                            <div className="relative aspect-[4/5] max-w-sm">
                                <div className="absolute inset-0 bg-[var(--danger)] -translate-x-4 -translate-y-4" />
                                <img
                                    src={SITE_META.avatarUrl}
                                    alt={`Retrato de ${SITE_META.authorName}, fundador de Autodestrucción S.A.`}
                                    className="relative w-full h-full object-cover border-2 border-[var(--paper-2)]"
                                    loading="lazy"
                                />
                                <Stamp
                                    size="md"
                                    rotation={-10}
                                    color="paper"
                                    className="absolute -bottom-3 -right-3"
                                >
                                    Evidencia 001
                                </Stamp>
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4">Sobre el fundador · 11</p>
                            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-6">
                                No soy un ejemplo.
                                <br />
                                Soy{" "}
                                <span className="text-[var(--danger)]">
                                    evidencia.
                                </span>
                            </h2>
                            <p className="font-mono text-base text-[var(--paper)] leading-relaxed mb-4">
                                Soy <strong className="text-[var(--paper-2)]">{SITE_META.authorName}</strong>, autor
                                chileno, fundador de SinAdicciones.org y PlanSobrio.com.
                            </p>
                            <p className="font-mono text-sm text-[var(--ink-muted)] leading-relaxed mb-8">
                                Escribo para entenderme, no para justificarme.
                                No busco dar lecciones ni vender una vida
                                perfecta. Mis cicatrices pidieron hablar y aquí
                                tienen tribuna.
                            </p>
                            <Link
                                to="/nelson-gonzalez-rodriguez"
                                className="btn-brutal btn-brutal-danger"
                                data-testid="sobre-preview-cta"
                            >
                                Leer quién soy
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
